import React from "react";
import PropTypes from "prop-types";
import {
  LuArrowLeft,
  LuCircleCheck,
  LuDownload,
  LuInfo,
  LuSend,
} from "react-icons/lu";
import { formatCurrencyAUD } from "utils/formatCurrency";

function formatFigmaDate(dateVal) {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (err) {
    return String(dateVal);
  }
}

export default function CustomerInvoicePreview({
  organisation = "Invoice & Recover",
  businessLogo = "",
  invoiceNumber = "Unsaved draft",
  status = "Draft · Preview only",
  customerName = "Customer",
  customerDetails = null,
  invoiceDate,
  dueDate,
  dueUpto = "14 days",
  currency = "AUD",
  gstType = "Inclusive",
  items = [],
  quotationNumber = "",
  purchaseOrderNumber = "",
  scopeNotes = "",
  invoiceNotes = "",
  paymentInstructions = "",
  bankAccountName = "",
  bankBsbCode = "",
  bankAccountNumber = "",
  subtotal = 0,
  totalDiscounts = 0,
  totalGST = 0,
  grandTotal = 0,
  payablePendingAmount = null,
  paymentsReceived = 0,
  appliedCredits = 0,
  onBack,
  backLabel = "Back to details",
  onSend,
  onDownload,
  canSend = false,
  isSaved = false,
  isSaving = false,
  isSending = false,
  isDownloading = false,
  darkMode = false,
  topBarVariant = "full", // "full" | "public" | "none"
  documentType = "invoice",
}) {
  const documentLabels = {
    invoice: { singular: "invoice", title: "Invoice", party: "Bill to" },
    quote: { singular: "quote", title: "Quotation", party: "Prepared for" },
    rcti: { singular: "RCTI", title: "RCTI", party: "Supplier" },
  }[documentType];
  const formattedInvoiceDate = formatFigmaDate(invoiceDate);
  const formattedDueDate = formatFigmaDate(dueDate);
  const currencyCode = /^[A-Z]{3}$/i.test(String(currency || ""))
    ? String(currency).toUpperCase()
    : "AUD";

  const displayInvoiceNumber =
    invoiceNumber && invoiceNumber !== "Auto Generated"
      ? invoiceNumber
      : "Unsaved draft";

  const validItems = items.filter(
    (item) =>
      item &&
      (item.itemID || item.itemName || item.itemDescription || item.itemPrice)
  );

  const paymentTermsText =
    dueUpto && dueDate
      ? `${dueUpto} from invoice date · Due ${formattedDueDate}`
      : formattedDueDate
      ? `Due ${formattedDueDate}`
      : "Due upon receipt";

  const noGst = gstType === "No GST";
  const isInclusive = gstType === "Inclusive" || gstType === undefined;

  const statusLower = String(status || "").toLowerCase();
  const isDraft =
    statusLower.includes("draft") || statusLower.includes("saved") || !status;
  const isPaid = statusLower === "paid";
  const isOverdue = statusLower === "overdue";
  const isAwaiting =
    statusLower.includes("awaiting") || (!isDraft && !isPaid && !isOverdue);

  let statusClass = "status-draft";
  let statusText = "Draft · Preview only";
  if (isPaid) {
    statusClass = "status-paid";
    statusText = "Paid";
  } else if (isOverdue) {
    statusClass = "status-overdue";
    statusText = "Overdue";
  } else if (isAwaiting && !isDraft) {
    statusClass = "status-awaiting";
    statusText = "Awaiting payment";
  }

  const numGrandTotal = parseFloat(grandTotal) || 0;
  const numSubtotal = parseFloat(subtotal) || 0;
  const numTotalDiscounts = parseFloat(totalDiscounts) || 0;
  const numTotalGst =
    totalGST !== undefined && totalGST !== null && !isNaN(parseFloat(totalGST))
      ? parseFloat(totalGST)
      : noGst
      ? 0
      : isInclusive
      ? numGrandTotal / 11
      : numGrandTotal * 0.1;

  const subtotalExcludingGst = noGst
    ? numGrandTotal
    : numSubtotal > 0 && numTotalGst > 0 && numSubtotal > numTotalGst
    ? numSubtotal - numTotalGst
    : Math.max(0, numGrandTotal - numTotalGst);

  const numPaymentsReceived =
    parseFloat(paymentsReceived) || (isPaid ? numGrandTotal : 0);
  const numAppliedCredits = parseFloat(appliedCredits) || 0;
  const remainingBalance =
    payablePendingAmount !== undefined &&
    payablePendingAmount !== null &&
    !isNaN(parseFloat(payablePendingAmount))
      ? parseFloat(payablePendingAmount)
      : isPaid
      ? 0
      : Math.max(0, numGrandTotal - numPaymentsReceived - numAppliedCredits);

  const invoiceReference = quotationNumber || purchaseOrderNumber || "";
  const hasBankDetails = Boolean(
    bankAccountName || bankBsbCode || bankAccountNumber
  );

  return (
    <div
      className={`customer-invoice-preview-page${darkMode ? " dark" : ""}`}
      data-testid="customer-invoice-preview"
    >
      {topBarVariant === "public" && (
        <div className="customer-invoice-preview-topbar public-document-action">
          <div className="preview-topbar-left">
            {onBack && (
              <button
                type="button"
                className="invoice-flow-back"
                onClick={onBack}
                aria-label="Back"
              >
                <LuArrowLeft />
                Back
              </button>
            )}
            <span className="invoice-context">
              {displayInvoiceNumber} · {customerName || "Customer"}
            </span>
          </div>

          <div className="customer-invoice-preview-actions">
            {onDownload && (
              <button
                type="button"
                className={`invoice-preview-btn${
                  isDownloading ? " is-loading btn-loading" : ""
                }`}
                disabled={isDownloading}
                onClick={onDownload}
                title="Download PDF"
              >
                {isDownloading ? (
                  <span className="btn-spinner" />
                ) : (
                  <LuDownload />
                )}
                <span>{isDownloading ? "Downloading..." : "Download PDF"}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {topBarVariant === "full" && (
        <>
          <button
            type="button"
            className="invoice-flow-back customer-preview-back"
            onClick={onBack}
            aria-label={backLabel}
          >
            <LuArrowLeft />
            {backLabel}
          </button>

          <div className="customer-invoice-preview-topbar customer-preview-identity-row">
            <div className="customer-preview-identity">
              <h1>Customer preview</h1>
              <div className="customer-preview-status-row">
                <span className="customer-preview-number">{displayInvoiceNumber}</span>
                <span className={`preview-status-pill ${statusClass}`}>
                  {isDraft ? "Draft" : statusText}
                </span>
                <span>{isDraft ? "Not sent" : statusText}</span>
              </div>
            </div>

            <div className="customer-invoice-preview-actions">
              {onDownload && (
                <button
                  type="button"
                  className={`invoice-preview-btn${
                    isDownloading ? " is-loading btn-loading" : ""
                  }`}
                  disabled={!isSaved || isDownloading || isSaving || isSending}
                  onClick={onDownload}
                  title="Download PDF"
                >
                  {isDownloading ? <span className="btn-spinner" /> : <LuDownload />}
                  <span>{isDownloading ? "Downloading..." : "Download PDF"}</span>
                </button>
              )}

              {onSend && (
                <button
                  type="button"
                  className={`invoice-preview-btn primary${
                    isSending ? " is-loading btn-loading" : ""
                  }`}
                  disabled={!canSend || isSending || isSaving}
                  onClick={onSend}
                >
                  {isSending ? <span className="btn-spinner" /> : <LuSend />}
                  <span>{isSending ? "Sending..." : `Send ${documentLabels.singular}`}</span>
                </button>
              )}
            </div>
          </div>

          <section className="customer-invoice-preview-feedback" aria-label="Preview guidance">
            <LuInfo aria-hidden="true" />
            <div>
              <p className="feedback-primary">Preview only · This draft has not been sent</p>
              <p className="feedback-secondary">
                Review the {documentLabels.singular} before sending it to {customerName || "the customer"}.
              </p>
              <p className="feedback-secondary">Confirm business details before sending.</p>
            </div>
          </section>
          {/* Legacy preview controls retained only for backwards CSS compatibility. */}
          <div className="customer-preview-legacy-controls" aria-hidden="true">
            <button
              type="button"
              className="invoice-flow-back"
              onClick={onBack}
              aria-label={backLabel}
            >
              <LuArrowLeft />
              {backLabel}
            </button>

            <div className="customer-invoice-preview-actions">
              {onDownload && (
                <button
                  type="button"
                  className={`invoice-preview-btn${
                    isDownloading ? " is-loading btn-loading" : ""
                  }`}
                  disabled={!isSaved || isDownloading || isSaving || isSending}
                  onClick={onDownload}
                  title="Download PDF"
                >
                  {isDownloading ? (
                    <span className="btn-spinner" />
                  ) : (
                    <LuDownload />
                  )}
                  <span>
                    {isDownloading ? "Downloading..." : "Download PDF"}
                  </span>
                </button>
              )}

              {onSend && (
                <button
                  type="button"
                  className={`invoice-preview-btn primary${
                    isSending ? " is-loading btn-loading" : ""
                  }`}
                  disabled={!canSend || isSending || isSaving}
                  onClick={onSend}
                  title={
                    !canSend
                      ? "Save draft and add valid recipient to send"
                      : "Send invoice"
                  }
                >
                  {isSending ? <span className="btn-spinner" /> : <LuSend />}
                  <span>
                    {isSending
                      ? "Sending..."
                      : `Send ${documentLabels.singular}`}
                  </span>
                </button>
              )}
            </div>
          </div>

          <header className="customer-invoice-preview-header">
            <h1>Customer {documentLabels.singular} preview</h1>
          </header>

          <section
            className="customer-invoice-preview-feedback"
            aria-label="Invoice status summary"
          >
            <p className="feedback-primary">{statusText} · Not sent</p>
            <p className="feedback-secondary">
              <span>{displayInvoiceNumber}</span>
              <span className="dot-sep">·</span>
              <span>{customerName || "No customer selected"}</span>
            </p>
          </section>
        </>
      )}

      <article
        className="customer-invoice-preview-card"
        data-pdf-ready={Boolean(
          isSaved && invoiceNumber && invoiceNumber !== "Auto Generated"
        )}
        aria-label={`Customer ${documentLabels.singular} document`}
      >
        <div className="preview-issuer-identity">
          <div className="preview-issuer-name">
            {businessLogo ? (
              <img className="preview-business-logo" src={businessLogo} alt={`${organisation} logo`} />
            ) : (
              <span className="preview-business-monogram" aria-hidden="true">
                {String(organisation || "I").trim().charAt(0).toUpperCase()}
              </span>
            )}
            <h2>{organisation || "Invoice & Recover"}</h2>
          </div>
          <div className="preview-doc-identity">
            <span className="preview-doc-title">{documentLabels.title}</span>
            <span className="preview-doc-number">{displayInvoiceNumber}</span>
          </div>
        </div>

        <hr className="preview-divider" />

        <div
          className={`preview-meta-grid${
            documentType === "quote" ? " quote-meta-grid" : ""
          }`}
        >
          <div className="preview-meta-cell">
            <span className="preview-meta-label">{documentLabels.party}</span>
            <strong className="preview-meta-value">
              {customerName || "Customer"}
            </strong>
            {customerDetails?.emailAddress && (
              <small className="preview-meta-sub">
                {customerDetails.emailAddress}
              </small>
            )}
            {customerDetails?.streetAddress && (
              <small className="preview-meta-sub">
                {customerDetails.streetAddress}
              </small>
            )}
          </div>
          <div className="preview-meta-cell">
            <span className="preview-meta-label">
              {documentType === "quote" ? "Quote date" : "Invoice date"}
            </span>
            <strong className="preview-meta-value">
              {formattedInvoiceDate}
            </strong>
          </div>
          <div className="preview-meta-cell">
            <span className="preview-meta-label">
              {documentType === "quote"
                ? "Validity"
                : "Due date"}
            </span>
            <strong className="preview-meta-value">
              {documentType === "quote"
                ? dueUpto || formattedDueDate
                : formattedDueDate}
            </strong>
          </div>
        </div>

        <div className="preview-items-section">
          <div className="preview-items-header">
            <h3>Items &amp; pricing</h3>
            <p>
              {noGst
                ? "No GST applies to this invoice."
                : isInclusive
                ? `Unit prices and line totals include GST · ${currencyCode}`
                : `Unit prices exclude GST; line totals include GST · ${currencyCode}`}
            </p>
          </div>

          <div className="preview-table-container">
            <table className="preview-items-table">
              <thead>
                <tr>
                  <th className="th-desc">Description</th>
                  <th className="th-qty">Qty</th>
                  <th className="th-price">Unit price</th>
                  <th className="th-gst">GST</th>
                  <th className="th-discount">Discount</th>
                  <th className="th-total">Line total</th>
                </tr>
              </thead>
              <tbody>
                {validItems.length > 0 ? (
                  validItems.map((item, idx) => {
                    const price = parseFloat(item.itemPrice) || 0;
                    const qty = parseFloat(item.itemQuantity) || 0;
                    const discount = parseFloat(item.discountAmount) || 0;
                    const lineTotal =
                      item.totalItemPrice !== undefined &&
                      item.totalItemPrice !== null &&
                      !isNaN(parseFloat(item.totalItemPrice))
                        ? parseFloat(item.totalItemPrice)
                        : Math.max(0, price * qty - discount);

                    const gstDisplay = noGst
                      ? "0%"
                      : item.gst !== undefined &&
                        item.gst !== null &&
                        item.gst !== ""
                      ? `${item.gst}%`
                      : "10%";

                    const discountDisplay =
                      discount > 0
                        ? `-$${formatCurrencyAUD(discount)}`
                        : item.discountValue > 0
                        ? ["%", "Percentage"].includes(item.discountType)
                          ? `${item.discountValue}%`
                          : `-$${formatCurrencyAUD(item.discountValue)}`
                        : "None";

                    return (
                      <tr key={item.itemID || item._id || idx}>
                        <td className="td-desc">
                          <strong className="item-name">
                            {item.itemName ||
                              item.itemDescription ||
                              "Unnamed item"}
                          </strong>
                          {item.itemName && item.itemDescription && (
                            <span className="item-sub-desc">
                              {item.itemDescription}
                            </span>
                          )}
                          {item.categoryCodeName && (
                            <span className="item-sub-desc item-account">
                              Account: {item.categoryCodeName}
                            </span>
                          )}
                        </td>
                        <td className="td-qty">{qty}</td>
                        <td className="td-price font-mono">
                          ${formatCurrencyAUD(price)}
                        </td>
                        <td className="td-gst">{gstDisplay}</td>
                        <td className="td-discount">{discountDisplay}</td>
                        <td className="td-total font-mono">
                          ${formatCurrencyAUD(lineTotal)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="td-empty">
                      No items entered on this invoice yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="preview-bottom-section">
          <div className="preview-notes-column">
            {documentType !== "quote" && invoiceReference ? (
              <div className="preview-note-group">
                <span className="preview-note-title">Invoice reference</span>
                <p className="preview-note-body">{invoiceReference}</p>
              </div>
            ) : null}

            {scopeNotes?.trim() ? (
              <div className="preview-note-group">
                <span className="preview-note-title">
                  Scope / customer notes
                </span>
                <p className="preview-note-body">{scopeNotes}</p>
              </div>
            ) : null}

            {invoiceNotes?.trim() ? (
              <div className="preview-note-group">
                <span className="preview-note-title">
                  {documentType === "quote" ? "Terms" : "Invoice notes"}
                </span>
                <p className="preview-note-body">{invoiceNotes}</p>
              </div>
            ) : null}

            {documentType !== "quote" && <div className="preview-note-group">
              <span className="preview-note-title">Payment terms</span>
              <p className="preview-note-body">{paymentTermsText}</p>
            </div>}

            {documentType !== "quote" && <div className="preview-note-group">
              <span className="preview-note-title">Payment instructions</span>
              <p className="preview-note-body">
                {paymentInstructions ||
                  `Please contact ${
                    organisation || "our office"
                  } for payment instructions`}
              </p>
            </div>}
          </div>

          <div className="preview-totals-column">
            <div className="preview-totals-row">
              <span className="totals-label">
                Subtotal excluding GST
              </span>
              <span className="totals-value font-mono">
                ${formatCurrencyAUD(subtotalExcludingGst)}
              </span>
            </div>

            <div className="preview-totals-row">
              <span className="totals-label">
                {noGst
                  ? "Total item discounts"
                  : `Total item discounts (${
                      isInclusive ? "GST inclusive" : "ex GST"
                    })`}
              </span>
              <span className="totals-value font-mono">
                ${formatCurrencyAUD(numTotalDiscounts)}
              </span>
            </div>

            <div className="preview-totals-row">
              <span className="totals-label">
                {noGst
                  ? "No GST"
                  : `GST 10% (${isInclusive ? "included" : "added"})`}
              </span>
              <span className="totals-value font-mono">
                ${formatCurrencyAUD(numTotalGst)}
              </span>
            </div>

            <hr className="totals-divider" />

            <div className="preview-totals-row grand-total">
              <span className="totals-label">
                {noGst ? "Total" : "Total including GST"} · {currencyCode}
              </span>
              <span className="totals-value font-mono grand-amount">
                ${formatCurrencyAUD(numGrandTotal)}
              </span>
            </div>

            <p className="preview-gst-clarification">
              {noGst
                ? "No GST applies to this invoice."
                : isInclusive
                ? "GST is included in the total, not added again."
                : "GST is calculated after discounts and added to the line totals. Discounts are already applied."}
            </p>

            {documentType !== "quote" && <hr className="totals-divider" />}

            {documentType !== "quote" && <div className="preview-totals-row">
              <span className="totals-label">Payments received</span>
              <span className="totals-value font-mono">
                ${formatCurrencyAUD(numPaymentsReceived)}
              </span>
            </div>}

            {documentType !== "quote" && <div className="preview-totals-row">
              <span className="totals-label">Applied credits</span>
              <span className="totals-value font-mono">
                ${formatCurrencyAUD(numAppliedCredits)}
              </span>
            </div>}

            {documentType !== "quote" && (isPaid ? (
              <div className="preview-settled-box">
                <div className="settled-header">
                  <LuCircleCheck />
                  <strong>Settled in full</strong>
                </div>
                <p>
                  Thank you. This invoice has been paid in full. There is no
                  remaining balance.
                </p>
              </div>
            ) : (
              <div className="preview-amount-due-box">
                <div className="amount-due-row">
                  <span className="amount-due-label">Amount due</span>
                  <strong className="amount-due-value font-mono">
                    ${formatCurrencyAUD(remainingBalance)}
                  </strong>
                </div>
                <span className="amount-due-sub">
                  {currencyCode} · Due {formattedDueDate}
                </span>
              </div>
            ))}
          </div>
        </div>

        {hasBankDetails && (
          <section className="preview-bank-details" aria-label="Bank details">
            <h3>Bank details</h3>
            <dl>
              {bankAccountName && (
                <div><dt>Account name</dt><dd>{bankAccountName}</dd></div>
              )}
              {bankBsbCode && (
                <div><dt>BSB</dt><dd>{bankBsbCode}</dd></div>
              )}
              {bankAccountNumber && (
                <div><dt>Account number</dt><dd>{bankAccountNumber}</dd></div>
              )}
            </dl>
          </section>
        )}

        {documentType !== "quote" && isDraft && (
          <p className="preview-card-footnote">
            Draft / preview only · Not issued. This document is not a request
            for payment.
          </p>
        )}
      </article>
    </div>
  );
}

CustomerInvoicePreview.propTypes = {
  organisation: PropTypes.string,
  businessLogo: PropTypes.string,
  invoiceNumber: PropTypes.string,
  status: PropTypes.string,
  customerName: PropTypes.string,
  customerDetails: PropTypes.object,
  invoiceDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  dueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  dueUpto: PropTypes.string,
  currency: PropTypes.string,
  gstType: PropTypes.string,
  items: PropTypes.array,
  quotationNumber: PropTypes.string,
  purchaseOrderNumber: PropTypes.string,
  scopeNotes: PropTypes.string,
  invoiceNotes: PropTypes.string,
  paymentInstructions: PropTypes.string,
  bankAccountName: PropTypes.string,
  bankBsbCode: PropTypes.string,
  bankAccountNumber: PropTypes.string,
  subtotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalDiscounts: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalGST: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  grandTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  payablePendingAmount: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  paymentsReceived: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  appliedCredits: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  onSend: PropTypes.func,
  onDownload: PropTypes.func,
  canSend: PropTypes.bool,
  isSaved: PropTypes.bool,
  isSaving: PropTypes.bool,
  isSending: PropTypes.bool,
  isDownloading: PropTypes.bool,
  darkMode: PropTypes.bool,
  topBarVariant: PropTypes.oneOf(["full", "public", "none"]),
  documentType: PropTypes.oneOf(["invoice", "quote", "rcti"]),
};
